import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import EntityUsersPage from '@features/user/pages/EntityUsersPage';
import EntityScopePicker, {
  EntityScopePickerChange,
} from '@features/entity/components/EntityScopePicker';
import { useEntityContext } from '@features/entity/hooks/usePermissions';
import {
  ENTITY_SCOPE_TOGGLE_CLASS,
  encodeEntityScopeDestination,
  formatEntityScopeLabel,
} from '@features/entity/constants/entityScopePicker';

interface ActiveScope {
  scope: string;
  scopeRefId: number;
  name?: string;
}

const NationUsersPage: React.FC = () => {
  const { nationId } = useParams<{ nationId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const sid = Number(nationId);
  const { context } = useEntityContext();

  const ownLabel = useMemo(() => {
    const name = context?.scopeName?.trim() || 'This Nation';
    return formatEntityScopeLabel('NATION', name.replace(/^Nation:\s*/i, ''));
  }, [context?.scopeName]);

  const [active, setActive] = useState<ActiveScope>({
    scope: 'NATION',
    scopeRefId: sid,
    name: ownLabel,
  });

  useEffect(() => {
    if (!(sid > 0)) return;
    setActive((prev) =>
      prev.scope === 'NATION' && prev.scopeRefId !== sid
        ? { scope: 'NATION', scopeRefId: sid, name: ownLabel }
        : prev.scope === 'NATION' && !prev.name
          ? { ...prev, name: ownLabel }
          : prev
    );
  }, [sid, ownLabel]);

  const destinationValue = useMemo(
    () => encodeEntityScopeDestination(active.scope, active.scopeRefId),
    [active.scope, active.scopeRefId]
  );

  const handleScopeChange = (next: EntityScopePickerChange) => {
    // Clear list URL state so remounted useSearch does not inherit filters from the prior entity.
    if (searchParams.toString()) {
      setSearchParams({}, { replace: true });
    }
    setActive({
      scope: next.scope,
      scopeRefId: next.scopeRefId,
      name: next.name,
    });
  };

  const scopeSelector =
    sid > 0 ? (
      <EntityScopePicker
        ownScopeValue="N"
        ownScopeRefId={sid}
        ownLabel={ownLabel}
        value={destinationValue}
        onChange={handleScopeChange}
        readOnlyWhenNoChildren={false}
        toggleClassName={ENTITY_SCOPE_TOGGLE_CLASS}
        testId="nation-users-scope-picker"
      />
    ) : null;

  const listScope = (active.scope.toUpperCase() || 'NATION') as
    | 'NATION'
    | 'DISTRICT'
    | 'CHURCH';

  return (
    <div key={`${active.scope}:${active.scopeRefId}`}>
      <EntityUsersPage
        scope={listScope}
        scopeRefId={active.scopeRefId}
        title="Users"
        subtitle="Manage people with roles at this nation, its districts, and churches"
        leadingControl={scopeSelector}
        testId="nation-users-search"
      />
    </div>
  );
};

export default NationUsersPage;
